import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_OPTIONS = [
  "AI Agent Building Certification ($199)",
  "AI Animation Course ($149.99)",
  "AI Coding Course ($99)",
  "AI Storybook Course",
  "AI Photo + Video Course",
  "Not sure yet — help me pick",
];

export default function LeadForm({ onSuccess }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    course_interest: COURSE_OPTIONS[0],
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Drop your name and email, sis.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Got it. Now let's pick your time.");
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something glitched. Try again in a sec."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="lead-form"
      className="bg-white border border-[#E5E5E5] shadow-hard-black p-6 md:p-8"
    >
      <div className="flex items-center gap-2 mb-6">
        <span className="inline-flex w-7 h-7 bg-black text-[#FFD60A] items-center justify-center font-bold text-sm">
          1
        </span>
        <p className="text-[11px] uppercase tracking-[0.3em] font-bold">
          Tell us about you
        </p>
      </div>

      <h3 className="font-display uppercase text-3xl md:text-4xl leading-[0.95] mb-2">
        Two minutes, then <span className="hi-yellow">we book it.</span>
      </h3>
      <p className="text-sm text-black/65 mb-8 max-w-md leading-relaxed">
        So we walk into your call ready — not asking the same questions twice.
        We don't sell your info. Promise.
      </p>

      <div className="space-y-4">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.25em] font-bold mb-2">
            Your Name
          </span>
          <input
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            placeholder="e.g. Tasha M."
            data-testid="lead-name"
            className="w-full bg-[#FAFAFA] border border-[#E5E5E5] focus:border-black focus:bg-white outline-none px-4 py-3.5 text-base transition-colors"
          />
        </label>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.25em] font-bold mb-2">
            Email
          </span>
          <input
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            placeholder="you@brand.com"
            data-testid="lead-email"
            className="w-full bg-[#FAFAFA] border border-[#E5E5E5] focus:border-black focus:bg-white outline-none px-4 py-3.5 text-base transition-colors"
          />
        </label>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.25em] font-bold mb-2">
            Which course are you eyeing?
          </span>
          <select
            value={form.course_interest}
            onChange={update("course_interest")}
            data-testid="lead-course"
            className="w-full bg-[#FAFAFA] border border-[#E5E5E5] focus:border-black focus:bg-white outline-none px-4 py-3.5 text-base transition-colors appearance-none"
          >
            {COURSE_OPTIONS.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.25em] font-bold mb-2">
            Anything we should know? <span className="text-black/40 normal-case tracking-normal">(optional)</span>
          </span>
          <textarea
            value={form.notes}
            onChange={update("notes")}
            rows={3}
            placeholder="A little context — your business, your goals, your timeline."
            data-testid="lead-notes"
            className="w-full bg-[#FAFAFA] border border-[#E5E5E5] focus:border-black focus:bg-white outline-none px-4 py-3.5 text-base transition-colors resize-none"
          />
        </label>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        <button
          type="submit"
          disabled={loading}
          data-testid="lead-submit"
          className="group inline-flex items-center gap-2 bg-[#FFD60A] text-black px-7 py-4 font-bold uppercase tracking-[0.2em] text-sm hover:bg-black hover:text-[#FFD60A] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Saving
            </>
          ) : (
            <>
              Continue to Booking <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => onSuccess?.()}
          data-testid="lead-skip"
          className="text-[11px] uppercase tracking-[0.25em] font-bold text-black/55 hover:text-black underline-offset-4 hover:underline"
        >
          Skip & go straight to Calendly →
        </button>
      </div>

      <div className="mt-8 pt-6 border-t border-[#E5E5E5] flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-black/50 font-bold">
        <CheckCircle2 className="w-4 h-4 text-black" />
        We never sell your info. Period.
      </div>
    </form>
  );
}
